import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useTranslations } from 'hooks/useTranslations';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { EArtAncillaryTypeName } from 'constants/ArtAccessoryType';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { StyledInfo } from 'pages/Firearms/RegisteredFirearmTableCard/styles';

interface IComponentsCard {
  data: ITableDataSource;
  handleEdit?: (item: ITableDataSource) => void;
  hasCustomFields: boolean;
  handleView?: (args: string) => void;
  renderColor: (data: ITableDataSource) => {
    border: string;
    stateField: EChipsVariants;
    background: string;
  };
  isAnyLicenceAuthorityValid: (licenceAuthorityIds: string[]) => boolean;
}

export enum EComponentsTableLabels {
  serialNumber = 'componentsTable.accordion.serialNumber.column',
  firecycleId = 'componentsTable.accordion.fireCycleId.column',
  manufacturer = 'componentsTable.accordion.manufacturer.column',
  productName = 'componentsTable.accordion.productName.column',
  exhibitNumber = 'componentsTable.accordion.exhibitNumber.column',
  componentType = 'componentsTable.accordion.componentType.column',
  firearmType = 'componentsTable.accordion.firearmType.column',
  owner = 'componentsTable.accordion.owner.column',
  keeper = 'componentsTable.accordion.keeper.column',
  componentState = 'componentsTable.accordion.componentState.column',
  make = 'componentsTable.accordion.make.column',
  model = 'componentsTable.accordion.model.column',
  type = 'componentsTable.accordion.componentType.column',
  state = 'componentsTable.accordion.state.column',
}

const ComponentsCard = ({
  data,
  handleEdit,
  hasCustomFields,
  handleView,
  renderColor,
  isAnyLicenceAuthorityValid,
}: IComponentsCard) => {
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();
  const navigate = useNavigate();

  const handleShowOnMap = () => {
    navigate(ERouteLinks.RegisteredAncillariesMap, {
      state: {
        ancillaryUniqueId: data?.id,
        ancillaryType: EArtAncillaryTypeName.COMPONENTS.toLowerCase(),
        legalityUniqueId: data?.legalityUniqueId,
        navigateBack: `${ERouteLinks.Ancillaries}?expand=component`,
        showAdvanced: true,
        showLegalityBar: false,
        breadcrumbs: [
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.component.name'),
            route: `${ERouteLinks.Ancillaries}?expand=component`,
          },
          t('ancillaries.ancillariesLocation.showOnMap.label'),
        ],
      },
    });
  };
  return (
    <Grid
      container
      sx={{
        border: `1px solid ${renderColor(data).border}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: renderColor(data).background,
      }}
    >
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.firecycleId)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.fireCycleId)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.serialNumber)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.serialNumber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.exhibitNumber)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.exhibitNumber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.type)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.ancillarySpecificType)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.manufacturer)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.manufacturer)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.productName)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.componentProductName)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.firearmType)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.componentFrtType)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.make)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.componentFrtMake)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.model)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.componentFrtModel)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.owner)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.ownerName)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.keeper)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.keeperName)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EComponentsTableLabels.state)}
        </Typography>
        {data.state ? <Chips text={data.state} variant={renderColor(data).stateField} /> : '-'}
      </Grid>

      {data.showInactiveOwnerOrKeeperWarning && (
        <Grid sx={{ p: 2 }}>
          <Grid sx={{ alignItems: 'flex-start', gap: 1, display: 'flex' }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('registeredFirearms.accordion.info.column')}
            </Typography>
            <Icon type={EIconTypes.infoWarning} fontSize={20} />
          </Grid>
          <StyledInfo size={{ xs: 12 }}>
            <span style={{ marginLeft: '10px' }}>
              {t('ancillaries.showInactiveOwnerOrKeeperWarning.text')}
            </span>
          </StyledInfo>
        </Grid>
      )}

      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          p: 2,
        }}
      >
        <Grid size={{ xs: 12, sm: 3 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('general.actions.button')}:{' '}
          </Typography>
        </Grid>

        <Grid container spacing={1} sx={{ justifyContent: 'flex-end' }}>
          {handleEdit &&
            hasCustomFields &&
            hasPermissions([EPermission.EDIT_ANCILLARY]) &&
            isAnyLicenceAuthorityValid(data.licenceAuthorityIds) && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  label={t('accesoriesTable.accordion.edit.button')}
                  variant={EButtonVariants.outlined}
                  onClick={() => handleEdit(data)}
                  size={EButtonSizes.small}
                  fullWidth
                />
              </Grid>
            )}
          {handleView && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                label={t('accesoriesTable.accordion.details.button')}
                variant={EButtonVariants.outlined}
                onClick={() => handleView(data.id as string)}
                size={EButtonSizes.small}
                fullWidth
              />
            </Grid>
          )}
          {data.isLatLong && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                label={t('registeredFirearms.firearmsDetails.showOnMap.button')}
                variant={EButtonVariants.contained}
                onClick={handleShowOnMap}
                size={EButtonSizes.small}
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export { ComponentsCard };
